import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { useTranslation } from '../i18n';

// STD-015: server state for the Reports domain. apiService is imported here
// (a *.queries.ts file) per STD-016 — components consume these hooks instead.
// Errors are toasted by the global QueryCache handler (see lib/queryClient);
// `meta.errorMessage` supplies the i18n fallback message.
export const reportKeys = {
  all: ['reports'] as const,
  list: () => [...reportKeys.all, 'list'] as const,
  filters: () => [...reportKeys.all, 'filters'] as const,
};

export function useReportsQuery() {
  const { t } = useTranslation();
  return useQuery({
    queryKey: reportKeys.list(),
    meta: { errorMessage: t('reports.failedToLoad') },
    queryFn: async () => {
      const res = await apiService.listReports();
      if (!res.success) throw new Error(res.error || 'Failed to load reports');
      return res.reports;
    },
  });
}

// Only fetched while the generate dialog is open (`enabled`); cached across
// reopens by the shared 60s staleTime.
export function useReportFiltersQuery(enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: reportKeys.filters(),
    enabled,
    meta: { errorMessage: t('reports.failedToLoadFilters') },
    queryFn: async () => {
      const res = await apiService.getReportFilters();
      if (!res.success) throw new Error(res.error || 'Failed to load report filters');
      return {
        drones: res.drones,
        payloads: res.payloads,
        pilots: res.pilots,
        maintenance_types: res.maintenance_types || [],
      };
    },
  });
}
